import { useMutation } from "@apollo/client";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { AppState } from "react-native";
import { Search as SearchIcon } from "@tamagui/lucide-icons";
import { InsuranceStatus } from "@medbillai/generated-graphql-types";
import { AnimatePresence, animateOnly, appear, Input, isWeb, useMedia, YStack } from "@medbillai/ui";
import { Flag } from "@medbillai/utils/flag";
import { type OnboardingNavProps, OnboardingScreen } from "../../components/OnboardingScreen";
import { analyticsService, type EventTypes } from "../../lib/analytics";
import { captureUnreportedError } from "../../lib/apollo/error";
import { useBoolVariation } from "../../lib/flag";
import { type OnDone, tagged } from "../../lib/flow-engine";
import { useKeyboardState } from "../../lib/keyboardController";
import { PayerResults } from "./PayerResults";
import type { OnboardingIdentity } from "./types";
import { recordInsuranceStatusMutation } from "./utils/queries";
import { usePayerSearch } from "./utils/usePayerSearch";
import { useRecordCarrier } from "./utils/useRecordCarrier";
export type InsuranceOutcomes = {
  selected: {
    carrier: string;
    payerId: string | null;
    /** The carrier is not recorded yet: the member ID step records both. */
    memberIdAsked: boolean;
  };
  uninsured: {};
  skipped: {};
};
const Outcome = tagged<InsuranceOutcomes>();
const MAX_TRACKED_TEXT_LENGTH = 40;
// No virtualization in the onboarding scroll region, so cap results.
const MAX_SEARCH_RESULTS = 30;
// Smallest viewport shrink read as an on-screen keyboard.
const KEYBOARD_MIN_HEIGHT = 120;
// Rounded down from the active payer catalog.
const SEARCHABLE_PAYERS = "3,500+";
export function Insurance({
  identity,
  nav,
  subtitle,
  awaitSave = false,
  askMemberId = true,
  onDone
}: {
  /**
   * Absent when the run never passed through Profile — the carrier is then
   * recorded without a Member row.
   */
  identity?: OnboardingIdentity;
  nav?: OnboardingNavProps;
  subtitle?: string;
  /** Finish only once the answer is saved, for a caller that reads it next. */
  awaitSave?: boolean;
  /** Off for a caller with no member ID step: the carrier is recorded here. */
  askMemberId?: boolean;
  onDone: OnDone<InsuranceOutcomes>;
}) {
  // Blocks a second tap from landing a second record.
  const [isSaving, setIsSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [namingCarrier, setNamingCarrier] = useState(false);
  const asksMemberId = useBoolVariation(Flag.INSURANCE_MEMBER_ID_ASK, false);
  const search = usePayerSearch({
    limit: MAX_SEARCH_RESULTS
  });
  const {
    searchText,
    setSearchText,
    settledSearch
  } = search;
  const [pendingPayerId, setPendingPayerId] = useState<string | null>(null);
  const recordCarrier = useRecordCarrier(identity);
  const [recordInsuranceStatus] = useMutation(recordInsuranceStatusMutation);
  const {
    gtSm
  } = useMedia();
  const typing = useSearchTyping();
  const listFirst = typing.active && !gtSm;
  const exit = usePickerTracking(settledSearch);
  const saveInBackground = (save: () => Promise<unknown>, done: () => void) => {
    setIsSaving(true);
    setSaveFailed(false);
    if (!awaitSave) {
      done();
    }
    void save().then(() => {
      if (awaitSave) {
        done();
      }
    }, (error: unknown) => {
      captureUnreportedError(error);
      setSaveFailed(awaitSave);
    }).finally(() => setIsSaving(false));
  };
  const pickCarrier = (carrier: string, payerId: string | null) => {
    if (isSaving) {
      return;
    }
    exit(payerId == null ? "notListed" : "picked", carrier);
    saveInBackground(() => recordCarrier(carrier, payerId), () => onDone(Outcome.selected({
      carrier,
      payerId,
      memberIdAsked: false
    })));
  };

  // Recorded so later surfaces stop asking for a card.
  const pickUninsured = () => {
    if (isSaving) {
      return;
    }
    exit("uninsured");
    saveInBackground(() => recordInsuranceStatus({
      variables: {
        input: {
          status: InsuranceStatus.UNINSURED
        }
      }
    }), () => onDone(Outcome.uninsured()));
  };
  const toNamingCarrier = (naming: boolean) => {
    setSaveFailed(false);
    setNamingCarrier(naming);
  };
  if (namingCarrier) {
    return <CarrierNameStep nav={nav} initialName={searchText.trim()} saving={isSaving} failed={saveFailed} onSubmit={carrier => pickCarrier(carrier, null)} onBack={() => toNamingCarrier(false)} />;
  }
  return <OnboardingScreen contained data-sentry-element="OnboardingScreen" data-sentry-component="Insurance" data-sentry-source-file="Insurance.tsx">
      <OnboardingScreen.Nav {...nav} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="Insurance.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="Insurance.tsx">
        <SlideAway hidden={listFirst} data-sentry-element="SlideAway" data-sentry-source-file="Insurance.tsx">
          <OnboardingScreen.Header alignment="left" title="Select your insurance" subtitle={subtitle} data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="Insurance.tsx" />
        </SlideAway>
        <YStack gap="$sp2" flex={1} $platform-web={{
        minHeight: 0
      }} data-sentry-element="YStack" data-sentry-source-file="Insurance.tsx">
          <Input testID="select-insurance.search" className="amp-unmask" label={`Search ${SEARCHABLE_PAYERS} insurers`} leftIcon={SearchIcon} value={searchText} onChangeText={setSearchText} onFocus={typing.onFocus} onBlur={typing.onBlur} autoCapitalize="none" autoComplete="off" autoCorrect={false} spellCheck={false} data-sentry-element="Input" data-sentry-source-file="Insurance.tsx" />
          <PayerResults search={search} pendingPayerId={awaitSave && isSaving ? pendingPayerId : null} failed={saveFailed} onPick={payer => {
          // A member ID step records the carrier with the ID, so the
          // first check carries it. No check runs without an identity,
          // so there is no ID to ask for.
          if (asksMemberId && askMemberId && payer.memberIdRequired && identity !== undefined) {
            exit("picked", payer.displayName);
            onDone(Outcome.selected({
              carrier: payer.displayName,
              payerId: payer.primaryPayerId,
              memberIdAsked: true
            }));
          } else {
            setPendingPayerId(payer.primaryPayerId);
            pickCarrier(payer.displayName, payer.primaryPayerId);
          }
        }} onNotListed={() => toNamingCarrier(true)} data-sentry-element="PayerResults" data-sentry-source-file="Insurance.tsx" />
        </YStack>
      </OnboardingScreen.Content>
      <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="Insurance.tsx">
        {listFirst && isWeb ? null : <YStack key="footer" width="100%" {...appear("fade")}>
            <OnboardingScreen.Footer>
              <OnboardingScreen.SecondaryAction testID="select-insurance.uninsured" onPress={pickUninsured}>
                I'm uninsured
              </OnboardingScreen.SecondaryAction>
              <OnboardingScreen.TertiaryAction testID="select-insurance.skip" onPress={() => {
            exit("skipped");
            onDone(Outcome.skipped());
          }}>
                Skip
              </OnboardingScreen.TertiaryAction>
            </OnboardingScreen.Footer>
          </YStack>}
      </AnimatePresence>
    </OnboardingScreen>;
}
type SettledSearch = EventTypes["InsurancePickerSearched"];
type PickerExit = EventTypes["InsurancePickerExited"]["outcome"];
function usePickerTracking(settled: SettledSearch | null) {
  const last = useRef<SettledSearch | null>(null);
  const queries = useRef(new Set<string>());
  const exited = useRef<PickerExit | null>(null);
  const query = settled?.query.slice(0, MAX_TRACKED_TEXT_LENGTH);
  const resultCount = settled?.resultCount;
  useEffect(() => {
    if (query == null || resultCount == null) {
      return;
    }
    last.current = {
      query,
      resultCount
    };
    queries.current.add(query.toLowerCase());
    analyticsService.trackEvent("InsurancePickerSearched", last.current);
  }, [query, resultCount]);
  const exit = useCallback((outcome: PickerExit, carrier: string | null = null) => {
    if (exited.current != null) {
      return;
    }
    exited.current = outcome;
    analyticsService.trackEvent("InsurancePickerExited", {
      outcome,
      carrier: carrier?.slice(0, MAX_TRACKED_TEXT_LENGTH) ?? null,
      lastQuery: last.current?.query ?? null,
      lastResultCount: last.current?.resultCount ?? null,
      distinctQueries: queries.current.size
    });
  }, []);
  useEffect(() => {
    const reopen = () => {
      if (exited.current === "abandoned") {
        exited.current = null;
      }
    };
    // StrictMode's remount runs the cleanup first.
    if (exited.current === "navigatedAway") {
      exited.current = null;
    }
    const subscription = AppState.addEventListener("change", state => {
      if (state === "background") {
        exit("abandoned");
      } else if (state === "active") {
        reopen();
      }
    });
    return () => {
      subscription.remove();
      exit("navigatedAway");
    };
  }, [exit]);
  return exit;
}

/**
 * True from search focus until the keyboard has finished closing, so nothing
 * moves under the tap that dismisses it. Blur alone isn't enough: Android's
 * back gesture closes the keyboard and leaves the field focused.
 */
function useSearchTyping() {
  const [active, setActive] = useState(false);
  const keyboardOpen = useRef(false);
  const nativeKeyboard = useKeyboardState().isVisible;
  useEffect(() => {
    if (isWeb) {
      return;
    }
    keyboardOpen.current = nativeKeyboard;
    if (!nativeKeyboard) {
      setActive(false);
    }
  }, [nativeKeyboard]);
  useEffect(() => {
    const viewport = isWeb ? window.visualViewport : null;
    if (!viewport) {
      return;
    }
    let width = viewport.width;
    let fullHeight = viewport.height;
    const onResize = () => {
      // Pinch-zoom shrinks the visual viewport too.
      if (viewport.scale !== 1) {
        return;
      }
      if (viewport.width !== width) {
        width = viewport.width;
        fullHeight = viewport.height;
      }
      fullHeight = Math.max(fullHeight, viewport.height);
      const open = viewport.height < fullHeight - KEYBOARD_MIN_HEIGHT;
      if (open && document.activeElement instanceof HTMLInputElement) {
        keyboardOpen.current = true;
        setActive(true);
      } else if (!open) {
        if (keyboardOpen.current) {
          setActive(false);
        }
        keyboardOpen.current = false;
      }
    };
    viewport.addEventListener("resize", onResize);
    return () => viewport.removeEventListener("resize", onResize);
  }, []);
  return {
    active,
    onFocus: () => setActive(true),
    onBlur: () => {
      if (!keyboardOpen.current) {
        setActive(false);
      }
    }
  };
}

/** Collapses to nothing so whatever sits below slides up into its place. */
function SlideAway({
  hidden,
  children
}: {
  hidden: boolean;
  children: ReactNode;
}) {
  const [height, setHeight] = useState<number>();
  return <YStack overflow="hidden" flexShrink={0} height={hidden ? 0 : height} opacity={hidden ? 0 : 1} aria-hidden={hidden} transition={{
    default: "expand",
    opacity: "fade"
  }} animateOnly={animateOnly("height", "opacity")} data-sentry-element="YStack" data-sentry-component="SlideAway" data-sentry-source-file="Insurance.tsx">
      {/* Out of flow once measured, so the box's height never squeezes what
          it measures: native shrinks an in-flow child to its parent. */}
      <YStack position={height === undefined ? "relative" : "absolute"} top={0} left={0} right={0} onLayout={e => setHeight(e.nativeEvent.layout.height)} data-sentry-element="YStack" data-sentry-source-file="Insurance.tsx">
        {children}
      </YStack>
    </YStack>;
}
function CarrierNameStep({
  nav,
  initialName,
  saving,
  failed,
  onSubmit,
  onBack
}: {
  nav?: OnboardingNavProps;
  initialName: string;
  saving: boolean;
  failed: boolean;
  onSubmit: (carrier: string) => void;
  onBack: () => void;
}) {
  const [name, setName] = useState(initialName);
  const carrier = name.trim();
  return <OnboardingScreen contained data-sentry-element="OnboardingScreen" data-sentry-component="CarrierNameStep" data-sentry-source-file="Insurance.tsx">
      <OnboardingScreen.Nav {...nav} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="Insurance.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="Insurance.tsx">
        <OnboardingScreen.Header alignment="left" title="What's your insurance called?" subtitle="Enter it as it appears on your insurance card." data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="Insurance.tsx" />
        <Input testID="select-insurance.carrier-name" className="amp-unmask" label="Insurance name" value={name} onChangeText={setName} autoFocus autoCorrect={false} data-sentry-element="Input" data-sentry-source-file="Insurance.tsx" />
        <OnboardingScreen.FormError message={failed ? "Something went wrong, please try again." : null} data-sentry-element="OnboardingScreen.FormError" data-sentry-source-file="Insurance.tsx" />
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="Insurance.tsx">
        <OnboardingScreen.PrimaryAction testID="select-insurance.carrier-name.continue" variant="brand" disabled={carrier === ""} loading={saving} onPress={() => onSubmit(carrier)} data-sentry-element="OnboardingScreen.PrimaryAction" data-sentry-source-file="Insurance.tsx">
          Continue
        </OnboardingScreen.PrimaryAction>
        <OnboardingScreen.TertiaryAction testID="select-insurance.carrier-name.back" onPress={onBack} data-sentry-element="OnboardingScreen.TertiaryAction" data-sentry-source-file="Insurance.tsx">
          Back to search
        </OnboardingScreen.TertiaryAction>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}